/**
 * Product Image Gallery Widget JavaScript
 * Ultra-lightweight and performant with Swiper
 * 
 * 100% Vanilla JavaScript - Zero jQuery dependency
 * Modern ES6+ with native DOM APIs
 *
 * @package Storedash_Checkout
 * @since 2.9.0
 * @updated 2025-10-30 - Migrated to vanilla JavaScript
 */

(function() {
    'use strict';

    // PERFORMANCE: Shared drag state for all galleries
    // Prevents multiple document-level listeners (3 galleries = 9 listeners → 3 listeners)
    const sharedDragState = {
        activeWrapper: null,      // Currently dragging wrapper element
        isDragging: false,
        /** Page X or Y at pointer down (mousemove uses same axis as delta) */
        startPointer: 0,
        scrollLeft: 0,
        scrollTop: 0,
        axis: 'x',               // 'x' for horizontal, 'y' for vertical
        initialized: false        // Flag to prevent duplicate document listeners
    };

    // Initialize shared document listeners once
    function initSharedDragListeners() {
        if (sharedDragState.initialized) return;
        sharedDragState.initialized = true;

        document.addEventListener('mousemove', (e) => {
            if (!sharedDragState.isDragging || !sharedDragState.activeWrapper) return;
            e.preventDefault();
            const wrapper = sharedDragState.activeWrapper;
            if (sharedDragState.axis === 'y') {
                const walk = e.pageY - sharedDragState.startPointer;
                wrapper.scrollTop = sharedDragState.scrollTop - walk;
            } else {
                const walk = e.pageX - sharedDragState.startPointer;
                wrapper.scrollLeft = sharedDragState.scrollLeft - walk;
            }
        });

        document.addEventListener('mouseup', () => {
            if (sharedDragState.isDragging && sharedDragState.activeWrapper) {
                sharedDragState.isDragging = false;
                sharedDragState.activeWrapper.style.scrollBehavior = 'smooth';
                sharedDragState.activeWrapper.style.cursor = 'grab';
                sharedDragState.activeWrapper = null;
            }
        });
    }

    class StoredashProductGallery {
        constructor(element) {
            this.element = element;
            this.mainGallery = element.querySelector('.main-gallery');
            this.thumbnails = element.querySelectorAll('.product-thumbnail');
            this.thumbnailsWrapper = element.querySelector('.product-thumbnails-wrapper');
            this.lightbox = element.querySelector('.product-lightbox');
            this.settings = this.getElementData(element, 'settings') || {};
            this.mainSwiper = null;
            this.lightboxSwiper = null;
            // Note: isDragging, startX, scrollLeft moved to sharedDragState for performance
            this._boundHandlers = {};
            this._abortController = new AbortController();

            this.init();
        }

        /**
         * Get data attribute value (replaces jQuery .data())
         */
        getElementData(element, key) {
            try {
                const value = element.dataset[key];
                return value ? JSON.parse(value) : null;
            } catch (e) {
                return element.dataset[key] || null;
            }
        }

        /**
         * Set data attribute value
         */
        setElementData(element, key, value) {
            element.dataset[key] = typeof value === 'object' ? JSON.stringify(value) : value;
        }

        init() {
            this.initMainGallery();
            this.initThumbnailSlider();
            this.applyVerticalThumbCount();
            this.initVerticalThumbnailNav();
            this.bindEvents();
        }

        /**
         * Get current breakpoint thumbnail count from per-breakpoint settings
         */
        getThumbsPerView() {
            const tpv = this.settings.thumbnailsPerView;
            // Support both legacy (number) and new (object) formats
            if (typeof tpv === 'number') return tpv;
            if (!tpv) return 4;
            if (window.innerWidth < 768) return tpv.mobile || tpv.tablet || tpv.desktop || 4;
            if (window.innerWidth < 1024) return tpv.tablet || tpv.desktop || 4;
            return tpv.desktop || 4;
        }

        /**
         * Get current breakpoint thumbnail gap
         */
        getThumbGap() {
            const gap = this.settings.thumbnailGap;
            if (typeof gap === 'number') return gap;
            if (!gap) return 10;
            if (window.innerWidth < 768) return gap.mobile || gap.tablet || gap.desktop || 10;
            if (window.innerWidth < 1024) return gap.tablet || gap.desktop || 10;
            return gap.desktop || 10;
        }

        /**
         * Set max-height on vertical thumbnail wrapper based on --thumbs-per-view
         * Only applies to left/right layouts on desktop (vertical thumbnails)
         */
        applyVerticalThumbCount() {
            if (!this.thumbnailsWrapper || !this.thumbnails.length) return;

            const gallery = this.element;
            const isVertical = gallery.classList.contains('gallery-layout-left') ||
                              gallery.classList.contains('gallery-layout-right');
            if (!isVertical) return;

            const apply = () => {
                // On mobile, left/right collapse to horizontal — CSS handles sizing, skip max-height
                if (window.innerWidth < 768) {
                    this.thumbnailsWrapper.style.maxHeight = '';
                    return;
                }

                const firstThumb = this.thumbnails[0];
                if (!firstThumb) return;

                const thumbHeight = firstThumb.getBoundingClientRect().height;
                if (thumbHeight <= 0) return; // not rendered yet

                const count = this.getThumbsPerView();
                const gap = this.getThumbGap();
                const maxHeight = count * thumbHeight + (count - 1) * gap;
                this.thumbnailsWrapper.style.maxHeight = maxHeight + 'px';
            };

            // Apply after images load (thumbnail height depends on image)
            const firstImg = this.thumbnails[0]?.querySelector('img');
            if (firstImg && !firstImg.complete) {
                firstImg.addEventListener('load', apply, { once: true });
            } else {
                apply();
            }

            // Re-apply on breakpoint changes
            const signal = this._abortController.signal;
            window.addEventListener('resize', () => {
                requestAnimationFrame(apply);
            }, { signal });
        }

        /**
         * Initialize main gallery Swiper
         */
        initMainGallery() {
            if (typeof Swiper === 'undefined') {
                return;
            }

            const config = {
                slidesPerView: 1,
                spaceBetween: 0,
                loop: false,
                grabCursor: this.settings.enableSwipe !== false,
                allowTouchMove: this.settings.enableSwipe !== false,
                speed: 400,
                navigation: {
                    nextEl: this.element.querySelector('.gallery-nav-next'),
                    prevEl: this.element.querySelector('.gallery-nav-prev'),
                },
                on: {
                    slideChange: this.handleSlideChange.bind(this),
                },
                // Performance: let browser handle lazy loading via native loading="lazy" attribute (Swiper 9+)
                watchSlidesProgress: true,
                preloadImages: false,
            };

            try {
                this.mainSwiper = new Swiper(this.mainGallery, config);
            } catch(e) {
            }
        }

        /**
         * Initialize lightbox
         * Generates slides on-demand for better initial page load performance
         */
        initLightbox() {
            if (typeof Swiper === 'undefined') {
                return;
            }

            if (!this.lightbox) {
                return;
            }

            const lightboxContainer = this.lightbox.querySelector('.lightbox-content');
            if (!lightboxContainer) {
                return;
            }

            // Generate lightbox slides from JSON data (lazy DOM creation)
            this.generateLightboxSlides(lightboxContainer);

            const config = {
                slidesPerView: 1,
                spaceBetween: 20,
                loop: false,
                grabCursor: true,
                speed: 400,
                navigation: {
                    nextEl: this.lightbox.querySelector('.swiper-button-next'),
                    prevEl: this.lightbox.querySelector('.swiper-button-prev'),
                },
                keyboard: {
                    enabled: true,
                    onlyInViewport: false,
                },
                observer: true,
                observeParents: true,
                on: {
                    slideChange: this.updateLightboxCounter.bind(this),
                },
            };

            try {
                this.lightboxSwiper = new Swiper(lightboxContainer, config);
                // Initialize lightbox thumbnail interactions after swiper is ready
                this.initLightboxThumbnails();
            } catch(e) {
            }
        }

        /**
         * Generate lightbox slides from JSON data
         * This delays DOM creation until lightbox is actually opened
         */
        generateLightboxSlides(container) {
            const wrapper = container.querySelector('.swiper-wrapper');
            if (!wrapper) return;

            // Don't regenerate if slides already exist
            if (wrapper.children.length > 0) return;

            // Get image data from JSON attribute
            const imagesJson = container.dataset.lightboxImages;
            if (!imagesJson) return;

            let images;
            try {
                images = JSON.parse(imagesJson);
            } catch(e) {
                return;
            }

            // Generate slides using document fragment for better performance
            const fragment = document.createDocumentFragment();
            images.forEach(image => {
                const slide = document.createElement('div');
                slide.className = 'swiper-slide';

                const img = document.createElement('img');
                img.src = image.url;
                img.alt = image.alt || '';
                img.loading = 'lazy';
                img.decoding = 'async';
                slide.appendChild(img);

                if (image.caption) {
                    const caption = document.createElement('div');
                    caption.className = 'image-caption';
                    caption.textContent = image.caption;
                    slide.appendChild(caption);
                }

                fragment.appendChild(slide);
            });

            wrapper.appendChild(fragment);
        }

        /**
         * Update lightbox navigation (counter or thumbnails)
         */
        updateLightboxNavigation() {
            if (!this.lightboxSwiper || !this.lightbox) return;

            const activeIndex = this.lightboxSwiper.activeIndex;

            // Update counter if present
            const counter = this.lightbox.querySelector('.lightbox-counter');
            if (counter) {
                const currentSlide = counter.querySelector('.current-slide');
                if (currentSlide) {
                    currentSlide.textContent = activeIndex + 1;
                }
            }

            // Update thumbnail strip if present
            this.updateLightboxThumbnails(activeIndex);
        }

        /**
         * Update lightbox thumbnail active state
         */
        updateLightboxThumbnails(activeIndex) {
            const strip = this.lightbox.querySelector('.lightbox-thumbnail-strip');
            if (!strip) return;

            const thumbs = strip.querySelectorAll('.lightbox-thumb');
            thumbs.forEach((thumb, idx) => {
                if (idx === activeIndex) {
                    thumb.classList.add('active');
                    this.scrollThumbnailIntoView(thumb, strip.querySelector('.lightbox-thumbnails-wrapper'));
                } else {
                    thumb.classList.remove('active');
                }
            });
        }

        /**
         * Scroll thumbnail into view (center it in the strip)
         */
        scrollThumbnailIntoView(thumb, container) {
            if (!thumb || !container) return;

            // Check for reduced motion preference
            const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

            // Calculate the scroll position to center the thumbnail
            const containerWidth = container.clientWidth;
            const thumbLeft = thumb.offsetLeft;
            const thumbWidth = thumb.offsetWidth;
            const scrollTarget = thumbLeft - (containerWidth / 2) + (thumbWidth / 2);

            container.scrollTo({
                left: Math.max(0, scrollTarget),
                behavior: prefersReducedMotion ? 'auto' : 'smooth'
            });
        }

        /**
         * Initialize lightbox thumbnail interactions
         */
        initLightboxThumbnails() {
            const strip = this.lightbox.querySelector('.lightbox-thumbnail-strip');
            if (!strip) return;

            const wrapper = strip.querySelector('.lightbox-thumbnails-wrapper');
            if (!wrapper) return;

            // Event delegation for thumbnail clicks
            wrapper.addEventListener('click', (e) => {
                const thumb = e.target.closest('.lightbox-thumb');
                if (!thumb) return;

                const index = parseInt(thumb.dataset.index, 10);
                if (!isNaN(index) && this.lightboxSwiper) {
                    this.lightboxSwiper.slideTo(index);
                }
            });

            // Initialize drag scrolling for the thumbnail strip
            this.initLightboxThumbnailDragScroll(wrapper);
        }

        /**
         * Initialize drag scroll for lightbox thumbnail strip
         * PERFORMANCE: Uses shared document listeners to prevent duplicates
         */
        initLightboxThumbnailDragScroll(wrapper) {
            this.initDragScroll(wrapper, 'x');
        }

        /**
         * Reusable drag-to-scroll for any wrapper element
         * @param {HTMLElement} wrapper - Scrollable container
         * @param {string|function(): string} axisOrGetter - 'x' / 'y', or function (for layout that flips at breakpoints)
         */
        initDragScroll(wrapper, axisOrGetter) {
            initSharedDragListeners();

            if (wrapper._storedashDragScrollBound) {
                return;
            }
            wrapper._storedashDragScrollBound = true;

            const resolveAxis = typeof axisOrGetter === 'function' ? axisOrGetter : () => axisOrGetter;

            wrapper.addEventListener('mousedown', (e) => {
                const axis = resolveAxis();
                if (e.button !== 0) return;
                sharedDragState.isDragging = true;
                sharedDragState.activeWrapper = wrapper;
                sharedDragState.axis = axis;
                sharedDragState.startPointer = axis === 'y' ? e.pageY : e.pageX;
                sharedDragState.scrollLeft = wrapper.scrollLeft;
                sharedDragState.scrollTop = wrapper.scrollTop;
                wrapper.style.scrollBehavior = 'auto';
                wrapper.style.cursor = 'grabbing';
            });

            wrapper.addEventListener('mouseleave', () => {
                if (sharedDragState.isDragging && sharedDragState.activeWrapper === wrapper) {
                    sharedDragState.isDragging = false;
                    sharedDragState.activeWrapper = null;
                    wrapper.style.scrollBehavior = 'smooth';
                    wrapper.style.cursor = 'grab';
                }
            });

            // Touch: use native overflow scrolling only. Custom touchmove fought the browser and caused jumps.
        }

        /**
         * Manage fade-start / fade-end CSS classes on a scrollable wrapper
         * @param {HTMLElement} wrapper - Scrollable container
         * @param {string|Function} axis - 'x', 'y', or a function returning 'x'/'y'
         * @returns {Function} updateFades - Call manually to refresh
         */
        initFadeIndicators(wrapper, axis) {
            const THRESHOLD = 5;
            let rafId = null;
            const getAxis = typeof axis === 'function' ? axis : () => axis;

            const updateFades = () => {
                const currentAxis = getAxis();
                if (currentAxis === 'y') {
                    const atTop = wrapper.scrollTop <= THRESHOLD;
                    const atBottom = wrapper.scrollTop + wrapper.clientHeight >= wrapper.scrollHeight - THRESHOLD;
                    wrapper.classList.toggle('fade-start', !atTop);
                    wrapper.classList.toggle('fade-end', !atBottom);
                } else {
                    const atLeft = wrapper.scrollLeft <= THRESHOLD;
                    const atRight = wrapper.scrollLeft + wrapper.clientWidth >= wrapper.scrollWidth - THRESHOLD;
                    wrapper.classList.toggle('fade-start', !atLeft);
                    wrapper.classList.toggle('fade-end', !atRight);
                }
            };

            const rafUpdate = () => {
                if (rafId) return;
                rafId = requestAnimationFrame(() => {
                    rafId = null;
                    updateFades();
                });
            };

            wrapper.addEventListener('scroll', rafUpdate, { passive: true });

            let resizeRaf = null;
            const signal = this._abortController.signal;
            window.addEventListener('resize', () => {
                if (resizeRaf) return;
                resizeRaf = requestAnimationFrame(() => {
                    resizeRaf = null;
                    updateFades();
                });
            }, { signal });

            // Initial check
            updateFades();

            return updateFades;
        }

        /**
         * Legacy method for backward compatibility
         */
        updateLightboxCounter() {
            this.updateLightboxNavigation();
        }

        /**
         * Initialize thumbnail drag-scroll and fade indicators for all layouts
         */
        initThumbnailSlider() {
            if (!this.thumbnailsWrapper) return;

            const wrapper = this.thumbnailsWrapper;
            const gallery = this.element.closest('.storedash-product-gallery') || this.element;

            // Detect layout from parent CSS class
            const isLeft = gallery.classList.contains('gallery-layout-left');
            const isRight = gallery.classList.contains('gallery-layout-right');
            const isBottom = gallery.classList.contains('gallery-layout-bottom');
            const isWrap = gallery.classList.contains('thumbs-mode-wrap');

            // Skip wrap mode — thumbnails wrap normally, no drag/fade needed
            if (isBottom && isWrap) return;
            // Bottom layout requires slider mode
            if (isBottom && this.settings.thumbnailDisplayMode !== 'slider') return;

            // Determine axis: vertical for left/right on desktop, horizontal otherwise
            // For left/right, axis can flip on resize, so use a dynamic getter
            const mql = window.matchMedia('(max-width: 768px)');
            const getAxis = (isLeft || isRight)
                ? () => mql.matches ? 'x' : 'y'
                : () => 'x';

            // Pass getAxis (not getAxis()) so drag uses current axis after resize; listeners attach once per wrapper
            this.initDragScroll(wrapper, getAxis);
            this.initFadeIndicators(wrapper, getAxis);
        }

        /**
         * Initialize vertical thumbnail navigation (for left/right layouts)
         */
        initVerticalThumbnailNav() {
            if (!this.thumbnailsWrapper) return;

            const wrapper = this.thumbnailsWrapper;
            const container = this.element.querySelector('.product-thumbnails-container');
            if (!container) return;

            const upBtn = container.querySelector('.thumbs-nav-up');
            const downBtn = container.querySelector('.thumbs-nav-down');

            if (!upBtn || !downBtn) return;

            const scrollAmount = 100; // Pixels to scroll per click

            // Check if thumbnails overflow and show/hide buttons accordingly
            const checkVerticalOverflow = () => {
                if (wrapper.scrollHeight > wrapper.clientHeight) {
                    container.classList.add('has-vertical-overflow');
                    upBtn.classList.toggle('disabled', wrapper.scrollTop === 0);
                    downBtn.classList.toggle('disabled', wrapper.scrollTop + wrapper.clientHeight >= wrapper.scrollHeight - 5);
                } else {
                    container.classList.remove('has-vertical-overflow');
                }
            };

            // Scroll up
            upBtn.addEventListener('click', (e) => {
                e.preventDefault();
                wrapper.scrollBy({
                    top: -scrollAmount,
                    behavior: 'smooth'
                });
                setTimeout(checkVerticalOverflow, 400);
            });

            // Scroll down
            downBtn.addEventListener('click', (e) => {
                e.preventDefault();
                wrapper.scrollBy({
                    top: scrollAmount,
                    behavior: 'smooth'
                });
                setTimeout(checkVerticalOverflow, 400);
            });

            // Update button states on scroll
            wrapper.addEventListener('scroll', checkVerticalOverflow);

            // Initial check
            checkVerticalOverflow();

            // Recheck on window resize
            // PERFORMANCE: Use requestAnimationFrame debounce to prevent thrashing
            let verticalResizeRaf = null;
            window.addEventListener('resize', () => {
                if (verticalResizeRaf) return;
                verticalResizeRaf = requestAnimationFrame(() => {
                    verticalResizeRaf = null;
                    checkVerticalOverflow();
                });
            }, { signal: this._abortController.signal });
        }

        /**
         * Bind events
         * All document-level handlers stored in _boundHandlers for cleanup in destroy()
         */
        bindEvents() {
            const self = this;
            const lightboxTrigger = this.settings.lightboxTrigger || 'icon';

            // Thumbnail clicks
            this.thumbnails.forEach(thumbnail => {
                thumbnail.addEventListener('click', this.handleThumbnailClick.bind(this));
            });

            // Enlarge button - ALWAYS opens lightbox
            const enlargeBtn = this.element.querySelector('.enlarge-btn');
            if (enlargeBtn) {
                enlargeBtn.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    self.openLightbox(e);
                });
            }

            // Main image click - depends on lightboxTrigger setting
            if (lightboxTrigger === 'image' || lightboxTrigger === 'both') {
                const mainSlides = this.element.querySelectorAll('.main-image-slide');
                mainSlides.forEach(slide => {
                    slide.addEventListener('click', (e) => {
                        // Don't open if clicking on the enlarge button
                        if (e.target.closest('.enlarge-btn')) return;
                        self.openLightbox(e);
                    });
                });
            }

            // Lightbox close + background click — single handler for both
            this._boundHandlers.documentClick = (e) => {
                if (!this.lightbox) return;
                if (e.target.closest('.product-lightbox .lightbox-close')) {
                    this.closeLightbox(e);
                    return;
                }
                if (!this.lightbox.classList.contains('active')) return;
                const isBackground = e.target.classList.contains('product-lightbox') ||
                                    e.target.classList.contains('lightbox-content') ||
                                    e.target.classList.contains('swiper-wrapper');
                if (isBackground) this.closeLightbox(e);
            };
            document.addEventListener('click', this._boundHandlers.documentClick);

            // ESC key to close lightbox
            this._boundHandlers.keydown = this.handleKeydown.bind(this);
            document.addEventListener('keydown', this._boundHandlers.keydown);

            // Listen for variation changes to update gallery
            this._boundHandlers.variationChanged = this.handleVariationChange.bind(this);
            document.addEventListener('storedash_variation_changed', this._boundHandlers.variationChanged);

            // Listen for variation reset to restore original images
            this._boundHandlers.variationReset = this.resetToOriginalImages.bind(this);
            document.addEventListener('storedash_variation_reset', this._boundHandlers.variationReset);
        }

        /**
         * Handle thumbnail click
         */
        handleThumbnailClick(e) {
            e.preventDefault();
            const thumbnail = e.currentTarget;
            const index = parseInt(thumbnail.dataset.index);

            // Update active state
            this.thumbnails.forEach(t => t.classList.remove('active'));
            thumbnail.classList.add('active');

            // Slide to corresponding image
            if (this.mainSwiper) {
                this.mainSwiper.slideTo(index);
            }
        }

        /**
         * Handle slide change - update active thumbnail
         */
        handleSlideChange() {
            if (!this.mainSwiper) return;

            const activeIndex = this.mainSwiper.activeIndex;
            this.thumbnails.forEach((t, idx) => {
                if (idx === activeIndex) {
                    t.classList.add('active');
                } else {
                    t.classList.remove('active');
                }
            });
        }

        /**
         * Open lightbox
         */
        openLightbox(e) {
            if (e) e.preventDefault();

            // Check if lightbox is enabled (handle boolean or undefined)
            if (this.settings.enableLightbox === false) {
                return;
            }

            if (!this.lightbox) {
                return;
            }

            // Get current slide index
            const currentIndex = this.mainSwiper ? this.mainSwiper.activeIndex : 0;

            // Initialize lightbox Swiper if not done yet
            if (!this.lightboxSwiper) {
                this.initLightbox();
            }

            // Set the correct slide BEFORE showing the lightbox to prevent flash
            if (this.lightboxSwiper) {
                this.lightboxSwiper.slideTo(currentIndex, 0);
                this.lightboxSwiper.update();
            }

            // Update counter to show current position
            this.updateLightboxCounter();

            // Show lightbox after setting the correct slide
            this.lightbox.classList.add('active');

            // PERFORMANCE FIX: Prevent layout shift when hiding scrollbar
            // Calculate scrollbar width and add compensating padding
            const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
            if (scrollbarWidth > 0) {
                document.body.style.paddingRight = scrollbarWidth + 'px';
            }
            document.body.style.overflow = 'hidden';
            document.body.classList.add('lightbox-open');

        }

        /**
         * Close lightbox
         */
        closeLightbox(e) {
            if (e) e.preventDefault();

            this.lightbox.classList.remove('active');
            document.body.style.overflow = '';
            document.body.style.paddingRight = ''; // Remove scrollbar compensation
            document.body.classList.remove('lightbox-open');
        }

        /**
         * Handle keyboard events
         */
        handleKeydown(e) {
            if (e.key === 'Escape' && this.lightbox && this.lightbox.classList.contains('active')) {
                this.closeLightbox();
            }
        }

        /**
         * Handle variation change - update gallery with variation image
         */
        handleVariationChange(event) {
            
            // Handle custom event details - detail is an array [variation, selectedAttributes]
            const detail = event.detail || [];
            const variationData = detail[0];

            if (!variationData || !variationData.image) {
                return;
            }

            // Get variation image data
            const variationImage = variationData.image;
            
            // WooCommerce get_available_variations() returns image with multiple size properties:
            // src, full_src, gallery_thumbnail_src, srcset, etc.
            const imageUrl = variationImage.src || variationImage.url;
            const imageSrcset = variationImage.srcset || '';
            // Try full_src first (full size), then large_src, fallback to src
            const largeImageUrl = variationImage.full_src || variationImage.large_src || imageUrl;

            if (!imageUrl) {
                return;
            }

            // Update first slide image with variation image
            const firstSlide = this.element.querySelector('.main-image-slide');
            const firstSlideImg = firstSlide ? firstSlide.querySelector('img') : null;

            if (firstSlideImg) {
                
                // Store original image data if not already stored
                // Note: dataset properties must be camelCase (originalSrc becomes data-original-src in HTML)
                if (!this.getElementData(firstSlideImg, 'originalSrc')) {
                    const originalSrc = firstSlideImg.getAttribute('src');
                    this.setElementData(firstSlideImg, 'originalSrc', originalSrc);
                    this.setElementData(firstSlideImg, 'originalLarge', 
                        this.getElementData(firstSlideImg, 'large') || originalSrc);
                }

                // Update image source
                firstSlideImg.setAttribute('src', imageUrl);
                if (imageSrcset) {
                    firstSlideImg.setAttribute('srcset', imageSrcset);
                }
                this.setElementData(firstSlideImg, 'large', largeImageUrl);

                // Update lightbox first slide if it exists
                if (this.lightbox) {
                    const lightboxFirstSlide = this.lightbox.querySelector('.swiper-slide');
                    const lightboxFirstImg = lightboxFirstSlide ? lightboxFirstSlide.querySelector('img') : null;
                    if (lightboxFirstImg) {
                        lightboxFirstImg.setAttribute('src', largeImageUrl);
                    }
                }

                // Slide to first image if not already there
                if (this.mainSwiper && this.mainSwiper.activeIndex !== 0) {
                    this.mainSwiper.slideTo(0, 300);
                }

                // Update active thumbnail
                this.thumbnails.forEach((t, idx) => {
                    if (idx === 0) {
                        t.classList.add('active');
                    } else {
                        t.classList.remove('active');
                    }
                });
                
            } else {
            }
        }

        /**
         * Reset to original product images (when variation is cleared)
         */
        resetToOriginalImages() {
            const firstSlide = this.element.querySelector('.main-image-slide');
            const firstSlideImg = firstSlide ? firstSlide.querySelector('img') : null;

            if (firstSlideImg && this.getElementData(firstSlideImg, 'originalSrc')) {
                firstSlideImg.setAttribute('src', this.getElementData(firstSlideImg, 'originalSrc'));
                this.setElementData(firstSlideImg, 'large', this.getElementData(firstSlideImg, 'originalLarge'));
                
                // Update lightbox
                if (this.lightbox) {
                    const lightboxFirstSlide = this.lightbox.querySelector('.swiper-slide');
                    const lightboxFirstImg = lightboxFirstSlide ? lightboxFirstSlide.querySelector('img') : null;
                    if (lightboxFirstImg) {
                        lightboxFirstImg.setAttribute('src', this.getElementData(firstSlideImg, 'originalLarge'));
                    }
                }
            }
        }

        /**
         * Destroy instance
         */
        destroy() {
            if (this.mainSwiper) {
                this.mainSwiper.destroy(true, true);
            }
            if (this.lightboxSwiper) {
                this.lightboxSwiper.destroy(true, true);
            }
            // Clean up document-level event listeners
            if (this._boundHandlers.documentClick) {
                document.removeEventListener('click', this._boundHandlers.documentClick);
            }
            if (this._boundHandlers.keydown) {
                document.removeEventListener('keydown', this._boundHandlers.keydown);
            }
            if (this._boundHandlers.variationChanged) {
                document.removeEventListener('storedash_variation_changed', this._boundHandlers.variationChanged);
            }
            if (this._boundHandlers.variationReset) {
                document.removeEventListener('storedash_variation_reset', this._boundHandlers.variationReset);
            }
            // Abort all signal-attached listeners (resize, etc.)
            if (this._abortController) {
                this._abortController.abort();
            }
        }
    }

    /**
     * Initialize on Elementor frontend
     */
    window.addEventListener('elementor/frontend/init', function() {
        if (typeof elementorFrontend !== 'undefined' && elementorFrontend.hooks) {
            elementorFrontend.hooks.addAction('frontend/element_ready/storedash-product-gallery.default', function($scope) {
                // $scope from Elementor is a jQuery object, convert to native element
                const element = $scope instanceof HTMLElement ? $scope : $scope[0];
                new StoredashProductGallery(element);
            });
        }
    });

    /**
     * Fallback for non-Elementor pages
     */
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initGalleries);
    } else {
        initGalleries();
    }

    function initGalleries() {
        if (typeof elementorFrontend === 'undefined') {
            const galleries = document.querySelectorAll('.elementor-widget-storedash-product-gallery, .brxe-storedash-product-gallery');
            galleries.forEach(gallery => {
                new StoredashProductGallery(gallery);
            });
        }
    }

    // Bricks Builder re-init function (called by Bricks when element renders/re-renders in editor)
    window.storedashBricksProductGallery = function(element) {
        if (!element) return;
        const existing = element._storedashGalleryInstance;
        if (existing && typeof existing.destroy === 'function') {
            existing.destroy();
        }
        new StoredashProductGallery(element);
    };

})();

